import { Directive, ElementRef, HostListener, Input, Renderer2 } from '@angular/core';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { ResizeDirection, ResizeDimension, ResizeMode } from '../enums';

@Directive({
  selector: '[appTextareaResize]',
  standalone: false
})
export class TextareaResizeDirective {
  @Input() minHeight: number = 100;
  @Input() minWidth: number = 200;
  @Input() resizeStep: number = 10;
  @Input('resizeMode') resizeMode: ResizeMode = ResizeMode.BOTH;

  constructor(
    private elementRef: ElementRef<HTMLTextAreaElement>,
    private renderer: Renderer2,
    private liveAnnouncer: LiveAnnouncer
  ) {}

  private announceResize(message: string): void {
    this.liveAnnouncer.announce(message, 'polite');
  }

  get canResizeVertical(): boolean {
    return this.resizeMode === ResizeMode.VERTICAL || this.resizeMode === ResizeMode.BOTH;
  }

  get canResizeHorizontal(): boolean {
    return this.resizeMode === ResizeMode.HORIZONTAL || this.resizeMode === ResizeMode.BOTH;
  }

  @HostListener('keydown', ['$event'])
  handleKeydown(event: KeyboardEvent): void {
    if (!(event.altKey && this.elementRef.nativeElement.tagName.toLowerCase() === 'textarea')) {
      return;
    }

    const textarea = this.elementRef.nativeElement;
    const currentHeight = textarea.offsetHeight;
    const currentWidth = textarea.offsetWidth;

    const resizeMap: Record<string, {
      dimension: ResizeDimension,
      direction: ResizeDirection,
      canResize: boolean,
      minValue?: number
    }> = {
      ArrowUp: { 
        dimension: ResizeDimension.HEIGHT, 
        direction: ResizeDirection.DECREASE, 
        canResize: this.canResizeVertical, 
        minValue: this.minHeight 
      },
      ArrowDown: { 
        dimension: ResizeDimension.HEIGHT, 
        direction: ResizeDirection.INCREASE, 
        canResize: this.canResizeVertical 
      },
      ArrowRight: { 
        dimension: ResizeDimension.WIDTH, 
        direction: ResizeDirection.INCREASE, 
        canResize: this.canResizeHorizontal 
      },
      ArrowLeft: { 
        dimension: ResizeDimension.WIDTH, 
        direction: ResizeDirection.DECREASE, 
        canResize: this.canResizeHorizontal, 
        minValue: this.minWidth 
      }
    };

    const config = resizeMap[event.key];
    if (!config || !config.canResize) return;

    const currentSize = config.dimension === ResizeDimension.HEIGHT ? currentHeight : currentWidth;
    let newSize = config.direction === ResizeDirection.INCREASE ? currentSize + this.resizeStep : currentSize - this.resizeStep;

    if (config.minValue !== undefined) {
      if (newSize < config.minValue) {
        this.announceResize(`Text area is at minimum ${config.dimension}.`);
        event.preventDefault();
        return;
      }
      newSize = Math.max(newSize, config.minValue);
    }

    this.renderer.setStyle(textarea, config.dimension, `${newSize}px`);
    this.announceResize(`Text area ${config.dimension} ${config.direction}d.`);

    event.preventDefault();
  }
}
